import {
  DEFAULT_GRADIENT_CONFIG,
  DEFAULT_ICON_COLOR,
  type IconGradientConfig,
} from '@/config/dmSuiteIconPalette';

export interface SvgLinearCoords {
  x1: string;
  y1: string;
  x2: string;
  y2: string;
}

export interface SvgRadialCoords {
  cx: string;
  cy: string;
  r: string;
  fx: string;
  fy: string;
}

/**
 * Calculates SVG linear gradient coordinates from angle in degrees.
 */
export function getLinearGradientCoords(angle = 135): SvgLinearCoords {
  const normalized = ((angle % 360) + 360) % 360;

  switch (normalized) {
    case 0:
      return { x1: '0%', y1: '50%', x2: '100%', y2: '50%' }; // Horizontal Left to Right
    case 90:
      return { x1: '50%', y1: '0%', x2: '50%', y2: '100%' }; // Vertical Top to Bottom
    case 135:
      return { x1: '0%', y1: '0%', x2: '100%', y2: '100%' }; // Diagonal Top-Left to Bottom-Right
    case 45:
      return { x1: '0%', y1: '100%', x2: '100%', y2: '0%' }; // Diagonal Bottom-Left to Top-Right
    case 180:
      return { x1: '100%', y1: '50%', x2: '0%', y2: '50%' };
    case 270:
      return { x1: '50%', y1: '100%', x2: '50%', y2: '0%' };
    default: {
      // General angle calculation in degrees
      const rad = ((angle - 90) * Math.PI) / 180;
      const x1 = Math.round(50 - Math.cos(rad) * 50);
      const y1 = Math.round(50 - Math.sin(rad) * 50);
      const x2 = Math.round(50 + Math.cos(rad) * 50);
      const y2 = Math.round(50 + Math.sin(rad) * 50);
      return {
        x1: `${x1}%`,
        y1: `${y1}%`,
        x2: `${x2}%`,
        y2: `${y2}%`,
      };
    }
  }
}

/**
 * Calculates SVG radial gradient coordinates.
 */
export function getRadialGradientCoords(): SvgRadialCoords {
  return {
    cx: '50%',
    cy: '50%',
    r: '70%',
    fx: '50%',
    fy: '50%',
  };
}

/**
 * Distributes stop offsets evenly across an array of color stops.
 */
export function getGradientStopOffsets(colors: string[]): Array<{ color: string; offset: string }> {
  if (!colors || colors.length === 0) {
    return [{ color: DEFAULT_ICON_COLOR, offset: '0%' }];
  }
  if (colors.length === 1) {
    return [
      { color: colors[0], offset: '0%' },
      { color: colors[0], offset: '100%' },
    ];
  }

  const step = 100 / (colors.length - 1);
  return colors.map((color, index) => {
    const rawOffset = index * step;
    const offset = `${Math.round(rawOffset * 10) / 10}%`;
    return { color, offset };
  });
}

/**
 * Normalizes gradient configuration or provides reliable fallback.
 */
export function normalizeGradientConfig(input?: unknown): IconGradientConfig {
  if (!input || typeof input !== 'object') {
    return { ...DEFAULT_GRADIENT_CONFIG, colors: [...DEFAULT_GRADIENT_CONFIG.colors] };
  }

  const raw = input as Record<string, unknown>;
  const type: 'linear' | 'radial' = raw.type === 'radial' ? 'radial' : 'linear';
  const angle = typeof raw.angle === 'number' ? raw.angle : 135;
  const preset = typeof raw.preset === 'string' ? raw.preset : null;

  let colors: string[] = [];
  if (Array.isArray(raw.colors) && raw.colors.length >= 2) {
    colors = raw.colors.filter((c) => typeof c === 'string' && c.trim().length > 0) as string[];
  }

  if (colors.length < 2) {
    colors = [...DEFAULT_GRADIENT_CONFIG.colors];
  }

  return {
    preset,
    type,
    angle,
    colors,
  };
}

/**
 * Generates CSS background string from gradient config for swatches / previews.
 */
export function buildCssGradientString(
  gradient?: IconGradientConfig | null,
  fallbackColor = DEFAULT_ICON_COLOR
): string {
  if (!gradient || !gradient.colors || gradient.colors.length < 2) {
    return fallbackColor;
  }

  const colorList = gradient.colors.join(', ');
  if (gradient.type === 'radial') {
    return `radial-gradient(circle at 50% 50%, ${colorList})`;
  }

  const angle = gradient.angle ?? 135;
  return `linear-gradient(${angle}deg, ${colorList})`;
}
